import React, { useState, useEffect, useRef } from 'react';
import {
  X, Upload, FileText, AlertCircle, CheckCircle2, Download, Loader2, Users, Building2, Tag, Sparkles, Zap, TrendingUp, ChevronDown,
  Clock, Activity, RefreshCw, Pause, Play, Calendar, BarChart3, User, Target, Shield, PhoneOff
} from 'lucide-react';
import Papa from 'papaparse';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

// Resolve the Express API base. Mirrors the helper used across the billing UI so
// the DNC scrub charge hits the same host as every other /billing route.
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api.getsurfox.com';
};

// Per-record price for the optional DNC + known-litigator scrub upsell. Kept in sync
// with DNC_SCRUB_PRICE_PER_RECORD_CENTS on the server (billing.js).
const DNC_SCRUB_PRICE_PER_RECORD = 0.05;

// Intelligent Field Detection System
const BulkImportStepIndicator = ({ selectedFile, mappingValid }) => {
  const steps = [
    {
      num: 1,
      label: 'Upload File',
      status: selectedFile ? 'complete' : 'current'
    },
    {
      num: 2,
      label: 'Map & Import',
      status: !selectedFile ? 'pending' : (mappingValid ? 'complete' : 'current')
    }
  ];

  return (
    <div className="mb-8 pb-6 border-b border-gray-200">
      <div className="flex items-center justify-center gap-4">
        {steps.map((step, idx) => (
          <React.Fragment key={idx}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                step.status === 'complete' ? 'bg-green-500 text-white shadow-sm' :
                step.status === 'current' ? 'bg-blue-500 text-white shadow-md' :
                'bg-gray-200 text-gray-500'
              }`}>
                {step.status === 'complete' ? '✓' : step.num}
              </div>
              <span className={`text-sm font-medium ${
                step.status === 'pending' ? 'text-gray-400' : 'text-gray-700'
              }`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 w-16 ${
                steps[idx + 1].status === 'complete' || steps[idx + 1].status === 'current' 
                  ? 'bg-blue-300' 
                  : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Intelligent Field Detection System
const FIELD_PATTERNS = {
  first_name: [
    'first name', 'firstname', 'fname', 'first', 'given name', 'given',
    'forename', 'first_name'
  ],
  last_name: [
    'last name', 'lastname', 'lname', 'last', 'surname', 'family name', 
    'family', 'sur name', 'last_name'
  ],
  full_name: [
    'name', 'full name', 'fullname', 'customer name', 'lead name', 'owner name',
    'owner', 'client', 'contact name', 'customer', 'person', 'individual', 
    'prospect', 'lead', 'contact', 'full_name', 'complete name'
  ],
  phone: [
    'phone', 'phone number', 'phonenumber', 'telephone', 'tel', 'cell',
    'mobile', 'contact', 'number', 'phone#', 'contact number', 'call',
    'cellular', 'home phone', 'work phone', 'mobile number', 'cell phone',
    'phone_number', 'contact_number'
  ],
  email: [
    'email', 'e-mail', 'email address', 'emailaddress', 'e-mail address',
    'contact email', 'mail', 'electronic mail', 'e_mail', 'email_address',
    'e-mail_address', 'mail address'
  ]
};



const detectFieldType = (columnName, sampleData) => {
  const name = columnName.toLowerCase().trim();
  const cleanData = sampleData.filter(val => val && val.toString().trim() !== '');
  
  if (cleanData.length === 0) return { type: null, confidence: 0 };
  
  // Check patterns for each field type - UPDATED KEYS
  const scores = {
    first_name: 0,
    last_name: 0,
    full_name: 0,
    phone: 0,
    email: 0
  };
  
  // ENHANCED: Pattern matching for column name with better exclusions
  Object.entries(FIELD_PATTERNS).forEach(([type, patterns]) => {
    patterns.forEach(pattern => {
      if (name.includes(pattern)) {
        scores[type] += (pattern.length / name.length) * 0.8;
      }
    });
  });
  
  // CRITICAL: Exclude obvious non-name columns by name
  const excludeFromName = [
    'city', 'state', 'zip', 'zipcode', 'postal', 'country', 'region',
    // 'county' and other admin regions read like proper names (Palm Beach,
    // Miami-Dade, Broward, Marion, Lee) and were being auto-mapped onto a name
    // field — the Exodus foreclosure county-name incident. Exclude them explicitly.
    'county', 'parish', 'borough', 'township', 'municipality', 'district',
    'address', 'street', 'avenue', 'road', 'lane', 'drive', 'blvd',
    'age', 'income', 'salary', 'price', 'value', 'amount', 'cost',
    'date', 'time', 'year', 'month', 'day',
    'bedrooms', 'bathrooms', 'sqft', 'square', 'feet', 'lot',
    'type', 'status', 'category', 'class', 'grade', 'level',
    'number', 'id', 'code', 'ref', 'reference'
  ];
  
  // If column name matches exclusion patterns, penalize ALL name-related scores
  if (excludeFromName.some(exclude => name.includes(exclude))) {
    console.log(`🚫 Column "${columnName}" excluded from name detection (matches: ${excludeFromName.filter(e => name.includes(e))})`);
    scores.first_name = -1.0;
    scores.last_name = -1.0;
    scores.full_name = -1.0;
  }
  
  // Data content analysis with improved detection
  cleanData.slice(0, 5).forEach(value => {
    const val = value.toString().toLowerCase().trim();
    
    // Email detection
    if (val.includes('@') && val.includes('.') && val.length > 5) {
      scores.email += 1.0;
      scores.first_name -= 0.5;
      scores.last_name -= 0.5;
      scores.full_name -= 0.5;
      scores.phone -= 0.5;
    }
    
    // Phone detection (improved pattern)
    const phonePattern = /^[\+\-\s\(\)\d]{7,}$/;
    const digitsOnly = val.replace(/[\s\-\(\)\+]/g, '');
    if (phonePattern.test(val) && digitsOnly.length >= 7 && digitsOnly.length <= 15) {
      scores.phone += 1.0;
      scores.first_name -= 0.3;
      scores.last_name -= 0.3;
      scores.full_name -= 0.3;
    }
    
    // Enhanced name detection with better exclusions
    const namePattern = /^[a-zA-Z\s\.\-\']{2,}$/;
    
    // Check for city/location patterns in the data
    const locationPatterns = [
      /^(orlando|miami|tampa|jacksonville|tallahassee|gainesville|fort|st\.|saint)$/i,
      /^(new york|los angeles|chicago|houston|phoenix|philadelphia|san antonio)$/i,
      /^(north|south|east|west)\s/i,
      /^(lake|port|mount|mt\.)\s/i,
      /^[A-Z][A-Z]$/,  // State codes like FL, CA, NY
      /^\d{5}$/        // ZIP codes
    ];
    
    if (namePattern.test(val) && val.length > 1) {
      // Check if it's likely a person name vs city/state/location
      let nameScore = 0.4;
      
      // Severely reduce score for obvious locations
      if (locationPatterns.some(pattern => pattern.test(val))) {
        console.log(`🏙️ Value "${val}" detected as location, reducing name score`);
        nameScore = -0.5;
      } else {
        // Check for typical name patterns
        const words = val.split(/\s+/);
        if (words.length >= 2) {
          // Multiple words = likely full name
          nameScore = 0.8;
          scores.full_name += nameScore;
        } else if (val.length >= 3) {
          // Single word = could be first or last name
          nameScore = 0.4;
          // Add to both first_name and last_name possibilities
          scores.first_name += nameScore * 0.5;
          scores.last_name += nameScore * 0.5;
          scores.full_name += nameScore * 0.3;
        }
      }
    }
  });
  
  // Find the highest scoring type
  const maxScore = Math.max(...Object.values(scores));
  const detectedType = Object.keys(scores).find(type => scores[type] === maxScore);
  
  // Only return a detection if the score is positive and above threshold
  const finalConfidence = maxScore > 0 ? Math.min(maxScore, 1.0) : 0;
  
  console.log(`🔍 Detection for "${columnName}":`, {
    scores,
    detectedType: finalConfidence > 0.3 ? detectedType : null,
    confidence: finalConfidence
  });
  
  return {
    type: finalConfidence > 0.3 ? detectedType : null,
    confidence: finalConfidence,
    scores
  };
};

const detectBestMappings = (csvHeaders, previewData) => {
  const detections = csvHeaders.map(header => ({
    column: header,
    ...detectFieldType(header, previewData.map(row => row[header]))
  }));
  
  const suggestions = {
    first_name: null,
    last_name: null,
    full_name: null,
    phone: null,
    email: null
  };
  
  ['first_name', 'last_name', 'full_name', 'phone', 'email'].forEach(fieldType => {
    const candidates = detections
      .filter(d => d.type === fieldType)
      .sort((a, b) => b.confidence - a.confidence);
    
    if (candidates.length > 0) {
      suggestions[fieldType] = candidates[0].column;
    }
  });
  
  // Determine mode
  if (suggestions.first_name && suggestions.last_name) {
    return { mode: 'split', suggestions, detections };
  }
  
  if (suggestions.full_name && !suggestions.first_name && !suggestions.last_name) {
    return { mode: 'full', suggestions, detections };
  }
  
  return { mode: 'manual', suggestions, detections };
};

// Run AI auto-detect on a freshly parsed CSV and return a partial mapping with
// any high-confidence fields filled in, plus the inferred name mode for UI
// visibility. Called from handleFileSelect so the mapping grid renders
// pre-populated AND only shows the rows that match the customer's actual data
// shape — no more "I don't have a Last Name column, what do I put here?".
const autoDetectMapping = (csvHeaders, csvPreviewData) => {
  const { suggestions: detected, detections: analysis } = detectBestMappings(csvHeaders, csvPreviewData);
  const confOf = (col) => analysis.find(d => d.column === col)?.confidence ?? 0;
  const auto = {};

  ['first_name', 'last_name', 'phone', 'email'].forEach(k => {
    if (detected[k] && confOf(detected[k]) > 0.7) auto[k] = detected[k];
  });

  if (detected.full_name && confOf(detected.full_name) > 0.7) {
    auto.full_name = detected.full_name;
  }

  // Name mode drives which name rows are visible in the mapping grid.
  // 'split' wins when separate first/last were detected (most common,
  // ~99% of CRM exports). 'full' wins when the CSV only carries one
  // combined name column — the user's exact pain case yesterday.
  // 'all' shows every row for manual mapping when nothing matches
  // confidently.
  let nameMode;
  if (auto.first_name || auto.last_name) {
    nameMode = 'split';
    // If we ALSO detected a separate full-name column, drop it from auto
    // so it doesn't render with a pre-filled value the user can't see.
    delete auto.full_name;
  } else if (auto.full_name) {
    nameMode = 'full';
  } else {
    nameMode = 'all';
  }

  return { auto, nameMode };
};

// Flat LinkedIn-style mapping grid — pure controlled component. The parent owns
// the `mapping` state and the canonical submit button (modal sticky footer); this
// component just renders dropdowns + preview. AI auto-detect runs upstream in
// handleFileSelect so the rows are pre-populated AND the right ones are shown.
//
// Visibility rules driven by `nameMode`:
//   - 'full'  → only Full Name row (customer has one combined column)
//   - 'split' → only First + Last rows (customer has separate columns — 99% case)
//   - 'all'   → every name row (auto-detect couldn't tell — manual fallback)
// In any mode the customer can hit "Show all name options" to expand to every
// row. Phone + Email always render.
//
// Strict mutual exclusion still applies: a column used in one slot disappears
// from every other slot's dropdown.
const IntelligentFieldMapping = ({ csvHeaders, csvPreviewData, mapping, onMappingChange, nameMode, onNameModeChange }) => {
  const ALL_FIELDS = [
    { key: 'full_name',  label: 'Full Name',     required: true,  group: 'name' },
    { key: 'first_name', label: 'First Name',    required: true,  group: 'name' },
    { key: 'last_name',  label: 'Last Name',     required: true,  group: 'name' },
    { key: 'phone',      label: 'Phone Number',  required: true,  group: 'contact' },
    { key: 'email',      label: 'Email Address', required: false, group: 'contact' },
  ];

  const visibleNameKeys = nameMode === 'full'
    ? ['full_name']
    : nameMode === 'split'
      ? ['first_name', 'last_name']
      : ['full_name', 'first_name', 'last_name'];
  const visibleFields = ALL_FIELDS.filter(f => f.group !== 'name' || visibleNameKeys.includes(f.key));

  const handleFieldChange = (fieldKey, csvColumn) => {
    onMappingChange({ ...mapping, [fieldKey]: csvColumn });
  };

  // Strict mutual exclusion — a CSV column used in ANY field is hidden from every
  // other field's options. The current field's own selection always stays visible.
  const getAvailableColumns = (fieldKey) => {
    const usedElsewhere = new Set();
    Object.entries(mapping).forEach(([k, v]) => {
      if (!v || k === fieldKey) return;
      usedElsewhere.add(v);
    });
    return csvHeaders.filter(h => !usedElsewhere.has(h));
  };

  const unmappedCount = csvHeaders.filter(h => !Object.values(mapping).includes(h)).length;

  return (
    <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Match your CSV columns to SurFox fields</h3>
        <p className="text-sm text-gray-500 mt-1">Inaccurate matches may affect import accuracy.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mb-3">
        {visibleFields.map((field) => {
          const available = getAvailableColumns(field.key);
          const value = mapping[field.key] || '';
          return (
            <div key={field.key} className="flex items-center gap-3">
              <span className="min-w-[130px] px-3 py-2 bg-gray-100 rounded-md text-sm font-medium text-gray-800 text-center">
                {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
              </span>
              <select
                value={value}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              >
                <option value="">Select a column</option>
                {available.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      {/* Escape hatch — only relevant when we're hiding name rows. Lets the
          customer override our auto-detect if it picked the wrong shape. */}
      {nameMode !== 'all' && (
        <div className="mb-6 text-xs text-gray-500">
          {nameMode === 'full' ? (
            <button
              type="button"
              onClick={() => onNameModeChange('split')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              My file has separate First and Last Name columns instead
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onNameModeChange('full')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              My file has a single Full Name column instead
            </button>
          )}
          <span className="text-gray-400 mx-2">·</span>
          <button
            type="button"
            onClick={() => onNameModeChange('all')}
            className="text-gray-600 hover:text-gray-800 font-medium"
          >
            Show all name options
          </button>
        </div>
      )}

      {unmappedCount > 0 && (
        <div className="mb-6 flex items-start gap-2 text-sm text-gray-600">
          <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p>
            We'll keep the other {unmappedCount} column{unmappedCount === 1 ? '' : 's'} with each lead.
            Your AI uses them as context in conversations, naturally weaving in details like company, role, or location when it helps.
          </p>
        </div>
      )}

      {csvPreviewData.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide font-medium text-gray-500 mb-2">Data preview</p>
          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {csvHeaders.slice(0, 6).map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-gray-600 border-r border-gray-200 last:border-r-0 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                  {csvHeaders.length > 6 && (
                    <th className="px-3 py-2 text-left font-medium text-gray-400 whitespace-nowrap">
                      +{csvHeaders.length - 6} more
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {csvPreviewData.slice(0, 3).map((row, idx) => (
                  <tr key={idx} className="border-t border-gray-200">
                    {csvHeaders.slice(0, 6).map((h) => (
                      <td key={h} className="px-3 py-2 text-gray-700 border-r border-gray-200 last:border-r-0 max-w-[180px] truncate">
                        {row[h] || <span className="text-gray-400 italic">empty</span>}
                      </td>
                    ))}
                    {csvHeaders.length > 6 && <td className="px-3 py-2 text-gray-400">...</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AddLead({ onSuccess, onCancel, initialTab = 'single' }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [leadFields, setLeadFields] = useState([]);
  const [parsedRecords, setParsedRecords] = useState([]);
  const [fileReady, setFileReady] = useState(false);
  
  // Upload Status Tab State
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState('');
  const [pollingActive, setPollingActive] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [hiddenJobIds, setHiddenJobIds] = useState(new Set());
  const [showAllJobs, setShowAllJobs] = useState(false);
  
  // Enhanced Mapping State Management
  const [showMappingInterface, setShowMappingInterface] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  // Drives which name slots show in the mapping grid. Set by auto-detect on
  // file load; user can override via the inline links in IntelligentFieldMapping.
  // 'split' = First + Last only, 'full' = Full Name only, 'all' = everything.
  const [nameMode, setNameMode] = useState('all');
  const [navigationHistory, setNavigationHistory] = useState([]);
  
  // Refs to persist data across renders
  const persistentDataRef = useRef({
    csvHeaders: [],
    csvPreviewData: [],
    parsedRecords: [],
    fieldMapping: {},
    selectedFile: null,
    showMappingInterface: false
  });

  // Starter plan: bulk imports are capped at 1,000 leads.
  const [isStarterCapped, setIsStarterCapped] = useState(false);

  // Scale plan: one-time educational nudge shown on first entry to Bulk Import,
  // encouraging leads to be spread across multiple campaigns so the full monthly
  // message allocation is actually reachable (one campaign only moves so fast).
  // Allocation is pulled live from plan_limits.sms_limit (varies per Scale tenant).
  const [isScalePlan, setIsScalePlan] = useState(false);
  const [smsAllocation, setSmsAllocation] = useState(null);
  const [showScaleBulkModal, setShowScaleBulkModal] = useState(false);
  const [dontShowScaleModal, setDontShowScaleModal] = useState(false);
  const scaleModalShownRef = useRef(false); // suppress repeat nags within a session

  // DNC scrub state — checkbox visibility is gated on all 3 conditions
  // (include_dnc + has_api_key + has_san). The actual API key / SAN are
  // never selected by the client; we only read presence booleans.
  const [dncEligible, setDncEligible] = useState(false);
  const [scrubDnc, setScrubDnc] = useState(false);

  // Interest CTA shown when the tenant is NOT eligible for DNC scrubbing.
  // Clicking it emails admin@surfox.ai (notify-dnc-interest edge function).
  const [dncInterestSending, setDncInterestSending] = useState(false);
  const [dncInterestSent, setDncInterestSent] = useState(false);
  const [dncInterestError, setDncInterestError] = useState('');

  // Form state
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    campaign_id: '',
    status: 'New Lead',
    property_address: '',
    notes: ''
  });

  const statuses = [
    { value: 'New Lead', icon: Sparkles, color: 'text-blue-600 bg-blue-50' },
    { value: 'Hot Lead', icon: Zap, color: 'text-red-600 bg-red-50' },
    { value: 'Cold Lead', icon: TrendingUp, color: 'text-gray-600 bg-gray-50' }
  ];

  // Enhanced navigation state management
  useEffect(() => {
    // Save state to sessionStorage for persistence.
    //
    // Deliberately does NOT persist parsedRecords / csvPreviewData. sessionStorage
    // caps at ~5MB per origin, and a real customer list (tens of thousands of rows
    // x every column) serializes to far more than that. Persisting them meant
    // JSON.stringify-ing the entire CSV on EVERY state change (each dropdown
    // touch), freezing the main thread, then throwing an uncaught
    // QuotaExceededError out of this effect — which unmounts the tree and reads to
    // the customer as "the website crashed". Restoring them was pointless anyway:
    // the File object can never be rehydrated, so the restore path already tells
    // the user to re-select the file.
    const currentState = {
      activeTab,
      csvHeaders,
      fieldMapping,
      nameMode,
      selectedFile: selectedFile ? {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type
      } : null,
      showMappingInterface,
      fileReady,
      uploadStatus,
      uploadError
    };

    // Never let persistence break the import. A full quota (or Safari private
    // mode, where setItem always throws) must not take the page down.
    try {
      sessionStorage.setItem('addLeadState', JSON.stringify(currentState));
    } catch (e) {
      console.warn('Could not persist Add Lead state (non-fatal):', e?.name || e);
      try { sessionStorage.removeItem('addLeadState'); } catch {}
    }

    // Update persistent ref
    persistentDataRef.current = {
      csvHeaders,
      csvPreviewData,
      parsedRecords,
      fieldMapping,
      selectedFile,
      showMappingInterface
    };
  }, [activeTab, csvHeaders, csvPreviewData, parsedRecords, fieldMapping, selectedFile, showMappingInterface, fileReady, uploadStatus, uploadError]);

  // Restore state on component mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('addLeadState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        
        // Only restore if we have meaningful data to restore
        const hasCSVData = parsedState.csvHeaders?.length > 0;
        const hasMapping = parsedState.fieldMapping && Object.keys(parsedState.fieldMapping).length > 0;
        const hasParsedRecords = parsedState.parsedRecords?.length > 0;
        
        // Only restore state if there's actually work in progress
        if (hasCSVData || hasMapping || hasParsedRecords) {
          console.log('🔄 Restoring saved state with data:', {
            csvHeaders: parsedState.csvHeaders?.length || 0,
            mapping: Object.keys(parsedState.fieldMapping || {}).length,
            records: parsedState.parsedRecords?.length || 0
          });
          
          if (parsedState.activeTab && parsedState.activeTab !== 'single') {
            setActiveTab(parsedState.activeTab);
          }
          
          if (parsedState.csvHeaders?.length > 0) {
            setCsvHeaders(parsedState.csvHeaders);
          }
          
          if (parsedState.csvPreviewData?.length > 0) {
            setCsvPreviewData(parsedState.csvPreviewData);
          }
          
          if (parsedState.parsedRecords?.length > 0) {
            setParsedRecords(parsedState.parsedRecords);
          }
          
          if (parsedState.fieldMapping && Object.keys(parsedState.fieldMapping).length > 0) {
            setFieldMapping(parsedState.fieldMapping);
          }

          if (parsedState.nameMode && ['full', 'split', 'all'].includes(parsedState.nameMode)) {
            setNameMode(parsedState.nameMode);
          }

          if (parsedState.showMappingInterface) {
            setShowMappingInterface(parsedState.showMappingInterface);
          }
          
          if (parsedState.fileReady) {
            setFileReady(parsedState.fileReady);
          }
          
          if (parsedState.uploadStatus) {
            setUploadStatus(parsedState.uploadStatus);
            setUploadError(parsedState.uploadError || false);
          }
          
          // Note: We can't restore the actual File object, but we can restore the metadata
          if (parsedState.selectedFile) {
            setUploadStatus(`📁 Previously selected: ${parsedState.selectedFile.name} (${(parsedState.selectedFile.size / 1024).toFixed(1)} KB) - Please re-select file to continue`);
          }
        } else {
          console.log('🗑️ Clearing empty saved state');
          sessionStorage.removeItem('addLeadState');
        }
      } catch (e) {
        console.warn('Failed to restore saved state:', e);
        sessionStorage.removeItem('addLeadState');
      }
    }
  }, []);

  // Track navigation history
  const trackNavigation = (from, to, data = {}) => {
    setNavigationHistory(prev => [...prev, {
      from,
      to,
      timestamp: Date.now(),
      data
    }]);
  };

  // Enhanced tab switching with state preservation
  // Per-user persistence for the Scale bulk-import education modal. Keyed on the
  // user id so "Don't show again" sticks for that specific user, not the browser.
  const scaleModalKey = (uid) => `surfox_scale_bulk_modal_dismissed:${uid || 'anon'}`;
  const scaleModalSuppressed = (uid) => {
    try { return localStorage.getItem(scaleModalKey(uid)) === '1'; }
    catch { return false; }
  };

  const handleTabSwitch = (newTab) => {
    trackNavigation(activeTab, newTab);
    setActiveTab(newTab);

    // Special handling for status tab
    if (newTab === 'status') {
      setJobsLoading(true);
      fetchUploadJobs();
    }

    // Scale-plan education: first time a Scale user opens Bulk Import this session,
    // nudge them to parallelize across campaigns. Permanently suppressed once they
    // check "Don't show again". Tab content still renders behind the modal.
    if (
      newTab === 'bulk' &&
      isScalePlan &&
      !scaleModalShownRef.current &&
      !scaleModalSuppressed(user?.id)
    ) {
      scaleModalShownRef.current = true;
      setShowScaleBulkModal(true);
    }
  };

  useEffect(() => {
    fetchLeadFields();
    fetchCampaigns();
  }, [user]);

  // Rest of your existing functions remain the same...
  const fetchUploadJobs = async () => {
    if (!user?.tenant_id || activeTab !== 'status') return;

    try {
      const { data, error } = await supabase
        .from('bulk_upload_jobs')
        .select('*')
        .eq('tenant_id', user.tenant_id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setJobs(data || []);
      setLastUpdated(new Date());
      setJobsError('');
    } catch (err) {
      console.error('Error fetching upload jobs:', err);
      setJobsError('Failed to load upload status');
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'status') {
      fetchUploadJobs();

      let interval;
      if (pollingActive) {
        interval = setInterval(fetchUploadJobs, 5000);
      }

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [user?.tenant_id, pollingActive, activeTab]);

  const fetchLeadFields = async () => {
    if (!user?.tenant_id) return;

    try {
      // First get the tenant's industry_id
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('industry_id')
        .eq('id', user.tenant_id)
        .single();

      if (tenantError) {
        console.error('Error fetching tenant:', tenantError);
        return;
      }

      if (!tenant?.industry_id) {
        console.log('No industry configured for tenant');
        return;
      }

      // Get field templates for this industry
      const { data, error } = await supabase
        .from('industry_field_templates')
        .select('*')
        .eq('industry_id', tenant.industry_id)
        .order('display_order');

      if (error) {
        console.error('Error fetching industry field templates:', error);
        return;
      }

      setLeadFields(data || []);
    } catch (err) {
      console.error('Error fetching lead fields:', err);
    }
  };

  const fetchCampaigns = async () => {
    if (!user || !user.tenant_id) {
      setLoadingCampaigns(false);
      return;
    }

    try {
      // Check plan (for the Starter bulk cap) + DNC eligibility flag in one go.
      // We deliberately do NOT select the actual blacklist_alliance_api_key / san values.
      const { data: tenant } = await supabase
        .from('tenants')
        .select('plan, include_dnc, plan_limits')
        .eq('id', user.tenant_id)
        .single();
      setIsStarterCapped(tenant?.plan === 'starter');
      setIsScalePlan(tenant?.plan === 'scale' || tenant?.plan === 'growth_plus');
      setSmsAllocation(tenant?.plan_limits?.sms_limit ?? null);

      // DNC eligibility is resolved server-side: platform master key (Internal
      // Tools) + this tenant's include_dnc + FTC SAN + ORG ID must all be present.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const res = await fetch(`${getApiBaseUrl()}/dnc/eligibility`, {
            headers: { 'Authorization': `Bearer ${session.access_token}` },
          });
          const elig = res.ok ? await res.json() : null;
          setDncEligible(!!elig?.eligible);
        } else {
          setDncEligible(false);
        }
      } catch (e) {
        console.warn('DNC eligibility check failed:', e);
        setDncEligible(false);
      }

      // Show all non-archived campaigns — matches the canonical /campaigns list
      // (filters on `archived`, not `is_active`). `is_active` is a separate
      // global_admin-gated flag that is false for virtually all normal customer
      // campaigns, so filtering on it wrongly hid them ("No campaigns found").
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name')
        .eq('tenant_id', user.tenant_id)
        .or('archived.is.null,archived.eq.false');

      if (error) {
        throw error;
      }
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) {
      alert('Please enter a campaign name');
      return;
    }

    setCreatingCampaign(true);
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('campaigns')
        .insert([
          {
            name: newCampaignName.trim(),
            tenant_id: user.tenant_id,
            is_active: true,
            created_at: now,
            start_date: now,
            // Voice AI v1 Phase 3 (strategic-planning/VOICE-AI-V1-HANDOFF.md §0.1).
            // Phase 10 (§6 slice 4) shipped surfAgent.js's teaching (executeCommitNewCampaign
            // now asks and sets this per-campaign) but DELIBERATELY did not touch this
            // shell: this same "create a campaign" modal is also the bulk-import target
            // picker's "new campaign" option, so a tenant creating one WHILE uploading a
            // CSV list needs it to stay bulk-eligible or the very upload they're mid-flow
            // on would be refused by processBulkUploadJob.js's own gate. This modal has no
            // way to know at click-time which of the two uses this is, so explicit,
            // unconditional true remains correct here — traced and confirmed, not a gap.
            allows_bulk_upload: true,
          }
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      setCampaigns([...campaigns, data]);
      setForm({ ...form, campaign_id: String(data.id) });
      setBulkCampaignId(String(data.id));
      setNewCampaignName('');
      setShowCreateCampaign(false);
      setShowCreateCampaignBulk(false);
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert(`Error creating campaign: ${error.message}`);
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setUploadStatus('Please upload a CSV file');
      setUploadError(true);
      return;
    }

    setSelectedFile(file);
    setUploadStatus('');
    setUploadError(false);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.data.length === 0) {
          setUploadStatus('CSV file appears to be empty');
          setUploadError(true);
          return;
        }

        const headers = Object.keys(results.data[0] || {});
        const records = results.data;
        setCsvHeaders(headers);
        setCsvPreviewData(records.slice(0, 5));
        setParsedRecords(records);

        // Run AI auto-detect upfront so the mapping grid renders pre-populated
        // AND only shows the rows that match the customer's actual data shape.
        const { auto, nameMode: detectedNameMode } = autoDetectMapping(headers, records.slice(0, 5));
        setFieldMapping({ full_name: '', first_name: '', last_name: '', phone: '', email: '', ...auto });
        setNameMode(detectedNameMode);

        trackNavigation('bulk', 'mapping', {
          recordCount: records.length,
          headerCount: headers.length
        });

        // Both flags flip together — there's no separate "review" step anymore.
        // The sticky bottom button is the only submit and it gates on mapping validity.
        setShowMappingInterface(true);
        setFileReady(true);
        setUploadStatus(`Found ${records.length.toLocaleString()} records. Review the matched fields below.`);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        setUploadStatus('Error parsing CSV file');
        setUploadError(true);
      }
    });
  };

  // Register interest in DNC/litigator scrubbing for tenants who don't have it.
  // Fires a notification email to admin@surfox.ai. Client state prevents
  // re-spamming within the session; one click is enough to flag it.
  const handleDncInterest = async () => {
    if (dncInterestSending || dncInterestSent) return;
    setDncInterestSending(true);
    setDncInterestError('');
    try {
      const { error } = await supabase.functions.invoke('notify-dnc-interest');
      if (error) throw error;
      setDncInterestSent(true);
    } catch (err) {
      console.error('Failed to register DNC interest:', err);
      setDncInterestError('Could not send your request. Please try again.');
    } finally {
      setDncInterestSending(false);
    }
  };

  const [showCreateCampaignBulk, setShowCreateCampaignBulk] = useState(false);
  const [bulkCampaignId, setBulkCampaignId] = useState('');

  // Compliance attestation gate. A blocking modal must be completed before any
  // bulk upload is written or uploaded. `attestRecordCount` is the exact number
  // of contacts the user is attesting to (post Starter-cap), shown in the modal
  // and persisted onto the job row via the RPC.
  const [showAttestModal, setShowAttestModal] = useState(false);
  const [attestChecked, setAttestChecked] = useState(false);
  const [attestRecordCount, setAttestRecordCount] = useState(0);

  // Resolve the exact set of records that will actually be uploaded. For Starter
  // plans this is the first 1,000 rows carrying both a name and a phone after the
  // mapping is applied; everyone else uploads every parsed row. Used both to
  // populate the attestation count and to build the upload payload, so the number
  // the user attests to is the number that ships.
  const getRecordsToUpload = () => {
    const finalMapping = { ...fieldMapping };
    const STARTER_LEAD_LIMIT = 1000;
    if (isStarterCapped && parsedRecords.length > 0) {
      const validRecords = [];
      for (const record of parsedRecords) {
        const hasPhone = finalMapping.phone && record[finalMapping.phone]?.toString().trim();
        const hasName =
          (finalMapping.full_name  && record[finalMapping.full_name]?.toString().trim()) ||
          (finalMapping.first_name && record[finalMapping.first_name]?.toString().trim()) ||
          (finalMapping.last_name  && record[finalMapping.last_name]?.toString().trim());
        if (hasPhone && hasName) {
          validRecords.push(record);
          if (validRecords.length >= STARTER_LEAD_LIMIT) break;
        }
      }
      return validRecords;
    }
    return parsedRecords;
  };

  // Confirm handler for the blocking attestation modal. Stamps the attestation
  // and re-invokes handleBulkSubmit to perform the actual upload. Only reachable
  // once the checkbox is checked.
  const handleConfirmAttestation = () => {
    if (!attestChecked) return;
    const attestation = {
      attested_at: new Date().toISOString(),
      attested_by: user.id,
      attested_record_count: attestRecordCount,
    };
    setShowAttestModal(false);
    handleBulkSubmit(attestation);
  };

  // Charge the tenant's card on file for the optional DNC/litigator scrub before the
  // upload is processed. Fetches a CSRF token then posts to the Express billing route.
  // Throws with a user-facing message on any failure so the caller can abort the upload.
  const chargeDncScrub = async (jobId, recordCount) => {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session?.access_token) {
      throw new Error('Your session expired. Please sign in again.');
    }

    const base = getApiBaseUrl();
    const csrfUrl = base.includes('/api') ? `${base}/csrf-token` : `${base}/api/csrf-token`;
    const csrfRes = await fetch(csrfUrl, {
      headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
    });
    if (!csrfRes.ok) throw new Error('Could not verify your session. Please try again.');
    const { csrfToken } = await csrfRes.json();

    const res = await fetch(`${base}/billing/charge-dnc-scrub`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ tenant_id: user.tenant_id, record_count: recordCount, job_id: jobId })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'The DNC scrub charge could not be processed.');
    }
    return data;
  };

  const handleBulkSubmit = async (attestation = null) => {
    if (!bulkCampaignId && campaigns.length > 0) {
      alert('Please select a campaign for the bulk import');
      return;
    }

    if (!selectedFile) {
      alert('Please select a CSV file to upload');
      return;
    }

    // Compliance attestation gate. Nothing is uploaded or written until the user
    // completes the blocking attestation modal. handleConfirmAttestation stamps
    // the attestation and calls back into this function with it.
    if (!attestation) {
      setAttestRecordCount(getRecordsToUpload().length);
      setAttestChecked(false);
      // DNC scrub upsell defaults on for eligible tenants; the decision (and its cost)
      // is confirmed inside the attestation modal.
      setScrubDnc(dncEligible);
      setShowAttestModal(true);
      return;
    }

    // Mapping is sent as-is — the processor handles full_name vs split-name
    // paths internally and calls Haiku for the full_name case. The pre-Haiku
    // workaround that rewrote first_name→full_name based on column-name
    // sniffing is gone; the UI now puts full-name columns in the full_name
    // slot directly.
    const finalMapping = { ...fieldMapping };

    // Starter plan: keep the first 1,000 rows with both a name and a phone.
    const recordsToUpload = getRecordsToUpload();

    setIsUploading(true);
    try {
      const fileName = `bulk-uploads/${user.tenant_id}/${Date.now()}-${selectedFile.name}`;

      // For Starter-capped tenants, upload only the capped record set rather than
      // the raw file so the edge-function processor never sees more than 1,000 rows.
      let fileToUpload = selectedFile;
      if (isStarterCapped && recordsToUpload.length > 0) {
        const csvString = Papa.unparse(recordsToUpload);
        fileToUpload = new Blob([csvString], { type: 'text/csv' });
      }

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('bulk-uploads')
        .upload(fileName, fileToUpload);

      if (uploadError) {
        throw new Error(`File upload failed: ${uploadError.message}`);
      }

      const { data: { publicUrl } } = supabase.storage
        .from('bulk-uploads')
        .getPublicUrl(fileName);

      const { data: jobData, error: jobError } = await supabase
        .rpc('create_bulk_upload_job', {
          p_tenant_id: user.tenant_id,
          p_created_by: user.id,
          p_file_name: selectedFile.name,
          p_file_url: publicUrl,
          p_file_size: selectedFile.size,
          p_campaign_id: bulkCampaignId || null,
          p_total_records: recordsToUpload.length,
          p_field_mapping: finalMapping,
          p_attested_at: attestation.attested_at,
          p_attested_by: attestation.attested_by,
          p_attested_record_count: attestation.attested_record_count
        });

      if (jobError) {
        throw new Error(`Failed to create upload job: ${jobError.message}`);
      }

      // DNC scrub decision. The RPC returns the new job's UUID as jobData.
      const jobId = jobData;

      if (scrubDnc && dncEligible) {
        // Opted in: flag the job for scrubbing, then charge the card on file for the
        // uploaded row count. The charge must clear BEFORE the processor runs. If it
        // fails we mark the job failed (status != 'pending' removes it from the queue)
        // and abort without invoking the processor, so nothing is uploaded.
        const { error: flagError } = await supabase
          .from('bulk_upload_jobs')
          .update({ scrub_dnc: true })
          .eq('id', jobId)
          .eq('tenant_id', user.tenant_id);
        if (flagError) {
          console.warn('⚠️ Failed to set scrub_dnc flag on job:', flagError);
        }

        setUploadStatus('Processing DNC scrub charge...');
        try {
          await chargeDncScrub(jobId, recordsToUpload.length);
        } catch (chargeErr) {
          console.error('DNC scrub charge failed:', chargeErr);
          await supabase
            .from('bulk_upload_jobs')
            .update({ status: 'failed', error_message: `DNC scrub charge failed: ${chargeErr.message}` })
            .eq('id', jobId)
            .eq('tenant_id', user.tenant_id);
          setUploadError(true);
          setUploadStatus(`We could not process the DNC scrub charge. ${chargeErr.message} Your list was not uploaded.`);
          setIsUploading(false);
          return;
        }
      } else if (dncEligible) {
        // Offered but toggled off: record the declined choice so it is logged.
        const { error: declineError } = await supabase
          .from('bulk_upload_jobs')
          .update({ dnc_scrub_declined: true })
          .eq('id', jobId)
          .eq('tenant_id', user.tenant_id);
        if (declineError) {
          console.warn('⚠️ Failed to log dnc_scrub_declined on job:', declineError);
        }
      }

      // Fire-and-forget trigger. The processor runs server-side for 60+
      // seconds (Haiku batches + chunk inserts), well past Supabase's HTTP
      // gateway timeout (~60s). If we awaited it, the browser would see a
      // 504 even though the function is still happily running. The user
      // tracks progress via the Upload Status tab (polls bulk_upload_jobs
      // every 5s) — no need to wait here.
      supabase.functions.invoke('bulk-upload-processor').catch((err) => {
        // The job is already in the queue. If this HTTP poke fails the
        // function still gets picked up by whatever periodically drains
        // the queue. Log only.
        console.warn('Processor trigger HTTP failed (job still queued):', err?.message || err);
      });

      setUploadStatus(`✅ Upload queued successfully! Processing ${parsedRecords.length} leads in background.`);
      setUploadError(false);

      // Save CSV columns to campaign for dynamic template placeholders
      if (bulkCampaignId && csvHeaders.length > 0) {
        try {
          console.log('💾 Saving available fields to campaign:', csvHeaders);

          const { error: fieldSaveError } = await supabase
            .from('campaigns')
            .update({
              available_fields: csvHeaders.map(col => ({
                csv_column: col,
                display_name: col
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase())
              }))
            })
            .eq('id', bulkCampaignId);

          if (fieldSaveError) {
            console.error('⚠️ Error saving available fields:', fieldSaveError);
          } else {
            console.log('✅ Available fields saved to campaign for dynamic templates');
          }
        } catch (fieldError) {
          console.error('⚠️ Error processing available fields:', fieldError);
        }
      }

      setSelectedFile(null);
      setParsedRecords([]);
      setFileReady(false);
      setBulkCampaignId('');
      setFieldMapping({});
      setCsvHeaders([]);
      setCsvPreviewData([]);
      setShowMappingInterface(false);
      setNameMode('all');
      setScrubDnc(false);

      // Clear session state after successful upload
      sessionStorage.removeItem('addLeadState');
      sessionStorage.removeItem('mappingCompleted');
      
      // Switch to status tab
      handleTabSwitch('status');
      fetchUploadJobs();
      
    } catch (err) {
      console.error('Bulk upload error:', err);
      setUploadStatus(`❌ ${err.message}`);
      setUploadError(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSingleSubmit = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!user || !user.tenant_id || !accessToken) {
        alert('User authentication information not available. Please log in.');
        return;
      }

      if (!form.campaign_id && campaigns.length > 0) {
        alert('Please select a campaign for the lead.');
        return;
      }

      const edgeFunctionUrl = 'https://wuuqrdlfgkasnwydyvgk.supabase.co/functions/v1/my-first-qualification-function'; 

      const res = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          status: form.status,
          tenant_id: user.tenant_id,
          campaign_id: form.campaign_id || null,
          property_address: form.property_address,
          notes: form.notes
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to submit lead');
      }

      setForm({
        name: '',
        phone: '',
        email: '',
        company: '',
        campaign_id: '',
        status: 'New Lead',
        property_address: '',
        notes: ''
      });
      
      onSuccess && onSuccess();
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const downloadTemplate = () => {
    // Updated template to reflect the intelligent mapping approach
    const basicTemplate = ['Full Name', 'Phone', 'Email Address', 'Company', 'Address', 'Age', 'Income', 'Property Value', 'Notes'];
    const sampleData = [
      'Jane Smith',
      '5551234567', 
      'jane.smith@email.com',
      'ABC Company',
      '456 Oak Street, Anytown, ST 12345',
      '35',
      '$75000',
      '$350000',
      'Interested in solar panels'
    ];

    const csvContent = [
      basicTemplate.join(','),
      sampleData.join(','),
      '', // Empty row for separation
      '# Instructions:',
      '# - Required: Name and Phone columns', 
      '# - Optional: Email column',
      '# - Column names are detected automatically',
      '# - All other columns will be preserved as additional data',
      '# - Remove this instruction section before uploading'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lead_import_template_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper functions for Upload Status tab
  const timeAgo = (date) => {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatEstimatedCompletion = (estimatedTime) => {
    if (!estimatedTime) return 'Calculating...';
    
    const now = new Date();
    const estimated = new Date(estimatedTime);
    const diffMs = estimated - now;
    
    if (diffMs <= 0) return 'Completing soon...';
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 60) return `~${diffMins} minutes`;
    return `~${diffHours}h ${diffMins % 60}m`;
  };

  const downloadFailedRows = async (jobId, fileName) => {
    try {
      const { data: errors, error } = await supabase
        .from('bulk_upload_errors')
        .select('*')
        .eq('job_id', jobId);

      if (error) throw error;

      if (!errors || errors.length === 0) {
        alert('No failed rows to download');
        return;
      }

      const headers = ['Row Number', 'Error Type', 'Error Message', 'Field', 'Original Data'];
      const csvData = errors.map(error => [
        error.row_number,
        error.error_type,
        error.error_message,
        error.error_field || '',
        JSON.stringify(error.raw_data)
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `failed_rows_${fileName}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading failed rows:', err);
      alert('Failed to download error report');
    }
  };

  const activeJobs = jobs.filter(job => ['pending', 'processing'].includes(job.status));
  const completedJobs = jobs.filter(job => ['completed', 'failed', 'cancelled'].includes(job.status));

  const visibleJobs = jobs.filter(job => !hiddenJobIds.has(job.id));
  const displayedJobs = showAllJobs ? visibleJobs : [
    ...visibleJobs.filter(job => ['pending', 'processing'].includes(job.status)),
    ...visibleJobs.filter(job => ['completed', 'failed', 'cancelled'].includes(job.status)).slice(0, 5)
  ];

  const hasMoreJobs = visibleJobs.length > displayedJobs.length;

  const dismissJob = (jobId) => {
    setHiddenJobIds(prev => new Set([...prev, jobId]));
  };

  // Enhanced cleanup on unmount
  useEffect(() => {
    return () => {
      // Only clear session storage if component is being unmounted due to cancel/close
      // Don't clear if it's just a re-render or navigation within the same session
      console.log('🧹 Component unmounting - clearing session data');
      sessionStorage.removeItem('addLeadState');
      sessionStorage.removeItem('mappingCompleted');
    };
  }, []);

  return (
    <>
      {/* Scale-plan education modal — one-time nudge to spread leads across
          campaigns. Sits above the Add Leads modal (z-[60] > z-50). */}
      {showScaleBulkModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowScaleBulkModal(false)}
            />
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Get the most out of your plan
              </h3>
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                {smsAllocation ? (
                  <>
                    Your Scale plan includes <strong>{smsAllocation.toLocaleString()}</strong> messages
                    per month. To hit that capacity, we recommend spreading your leads across multiple
                    campaigns so conversations run in parallel. One campaign can only move so fast.
                  </>
                ) : (
                  <>
                    To get the most out of your plan, we recommend spreading your leads across multiple
                    campaigns so conversations run in parallel. One campaign can only move so fast.
                  </>
                )}
              </p>
              <label className="flex items-center gap-2 mt-5 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={dontShowScaleModal}
                  onChange={(e) => setDontShowScaleModal(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Don't show this again
              </label>
              <button
                onClick={() => {
                  if (dontShowScaleModal) {
                    try { localStorage.setItem(scaleModalKey(user?.id), '1'); } catch {}
                  }
                  setShowScaleBulkModal(false);
                }}
                className="mt-5 w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance attestation gate — blocking. No close button and no
          backdrop dismiss: the only way forward is checking the box and
          confirming. Sits above everything (z-[70]). */}
      {showAttestModal && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Non-dismissing backdrop — intentionally has no onClick. */}
            <div className="fixed inset-0 bg-black bg-opacity-60" />
            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mx-auto mb-4">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-1">
                Compliance attestation
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                You are about to upload <strong className="text-gray-900">{attestRecordCount.toLocaleString()}</strong> {attestRecordCount === 1 ? 'contact' : 'contacts'}.
              </p>

              {/* DNC scrub upsell. Optional, defaults on for eligible tenants, and
                  charges the card on file before processing. Turning it off is logged. */}
              {dncEligible && (
                <label className="flex items-start gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={scrubDnc}
                    onChange={(e) => setScrubDnc(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="flex-1 text-sm text-amber-900">
                    <span className="font-semibold flex items-center gap-1.5">
                      <Shield className="w-4 h-4" />
                      Scrub this list for DNC and known litigators
                    </span>
                    <span className="block mt-1 text-amber-800">
                      {attestRecordCount.toLocaleString()} {attestRecordCount === 1 ? 'contact' : 'contacts'} x ${DNC_SCRUB_PRICE_PER_RECORD.toFixed(2)} = <strong>${(attestRecordCount * DNC_SCRUB_PRICE_PER_RECORD).toFixed(2)}</strong>
                    </span>
                    <span className="block mt-1 text-xs text-amber-700">
                      Numbers on the National DNC or known TCPA litigator lists are excluded before import and provided as a downloadable report. Your card on file is charged before processing begins.
                    </span>
                  </span>
                </label>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
                By proceeding, I declare that the contacts in this upload have explicitly opted in to receive SMS messages from my business specifically. I did not purchase, rent, or obtain this list from a third-party data broker or list vendor. I understand that SurFox AI bears no liability for my messaging practices and that I am solely responsible for TCPA compliance.
              </div>

              <label className="flex items-start gap-3 mt-4 text-sm text-gray-800 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={attestChecked}
                  onChange={(e) => setAttestChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="font-medium">I understand and declare this is true</span>
              </label>

              <button
                onClick={handleConfirmAttestation}
                disabled={!attestChecked}
                className="mt-5 w-full px-4 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Confirm and continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onCancel}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add Leads</h2>
              <button
                onClick={onCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Enhanced Tab Navigation with State Indicators */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                <button
                  onClick={() => handleTabSwitch('single')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'single'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Users className="w-4 h-4 inline-block mr-2" />
                  Single Entry
                </button>
                <button
                  onClick={() => handleTabSwitch('bulk')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors relative ${
                    activeTab === 'bulk'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Upload className="w-4 h-4 inline-block mr-2" />
                  Bulk Import
                  {(fileReady || Object.keys(fieldMapping).length > 0) && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" />
                  )}
                </button>
                <button
                  onClick={() => handleTabSwitch('status')}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'status'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Activity className="w-4 h-4 inline-block mr-2" />
                  Upload Status
                  {activeJobs.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                      {activeJobs.length}
                    </span>
                  )}
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
              {activeTab === 'single' ? (
                /* Single Entry Form - Keep existing implementation */
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1 (555) 123-4567"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Property Address
                      </label>
                      <input
                        type="text"
                        value={form.property_address}
                        onChange={(e) => setForm({ ...form, property_address: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="123 Main St, City, State"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="New Lead">New Lead</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Campaign
                      </label>
                      {loadingCampaigns ? (
                        <p className="text-sm text-gray-500">Loading campaigns...</p>
                      ) : campaigns.length === 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-500">No campaigns found</p>
                          <button
                            type="button"
                            onClick={() => setShowCreateCampaign(!showCreateCampaign)}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Create new campaign
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <select
                            value={form.campaign_id}
                            onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Select a campaign</option>
                            {campaigns.map(campaign => (
                              <option key={campaign.id} value={String(campaign.id)}>
                                {campaign.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowCreateCampaign(!showCreateCampaign)}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Create new campaign
                          </button>
                        </div>
                      )}
                      
                      {showCreateCampaign && (
                        <div className="mt-2 flex gap-2">
                          <input
                            type="text"
                            value={newCampaignName}
                            onChange={(e) => setNewCampaignName(e.target.value)}
                            placeholder="Campaign name"
                            className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                            disabled={creatingCampaign}
                          />
                          <button
                            type="button"
                            onClick={handleCreateCampaign}
                            disabled={creatingCampaign}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                          >
                            {creatingCampaign ? 'Creating...' : 'Create'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                </div>
              ) : activeTab === 'bulk' ? (
                /* Enhanced Bulk Import with clearer UI flow */
                <div className="space-y-6">

                  {/* STARTER PLAN CAP BANNER */}
                  {isStarterCapped && (
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                      <span>
                        <strong>Starter plan imports are capped at 1,000 leads.</strong> If your file has more, only the first 1,000 will be imported. Upgrade for higher limits.
                      </span>
                    </div>
                  )}

                  {/* STEP INDICATOR */}
                  <BulkImportStepIndicator
                    selectedFile={selectedFile}
                    mappingValid={!!fieldMapping.phone && (!!fieldMapping.full_name || !!fieldMapping.first_name || !!fieldMapping.last_name)}
                  />

                  {/* CAMPAIGN SELECTION - ALWAYS VISIBLE AT TOP */}
                  <div className={`rounded-lg p-4 ${bulkCampaignId ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                    <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Campaign for Import *
                    </label>
                    {loadingCampaigns ? (
                      <p className="text-sm text-gray-500">Loading campaigns...</p>
                    ) : campaigns.length === 0 ? (
                      <div className="space-y-3">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-sm text-yellow-800 mb-2">No campaigns found. Create your first campaign to start importing leads.</p>
                          <button
                            type="button"
                            onClick={() => setShowCreateCampaignBulk(!showCreateCampaignBulk)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {showCreateCampaignBulk ? 'Cancel' : '+ Create Campaign'}
                          </button>
                        </div>
                        
                        {showCreateCampaignBulk && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newCampaignName}
                              onChange={(e) => setNewCampaignName(e.target.value)}
                              placeholder="Campaign name"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              disabled={creatingCampaign}
                            />
                            <button
                              type="button"
                              onClick={handleCreateCampaign}
                              disabled={creatingCampaign}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                            >
                              {creatingCampaign ? 'Creating...' : 'Create'}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          value={bulkCampaignId}
                          onChange={(e) => setBulkCampaignId(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-base"
                          required
                        >
                          <option value="">Select a campaign...</option>
                          {campaigns.map(campaign => (
                            <option key={campaign.id} value={String(campaign.id)}>
                              {campaign.name}
                            </option>
                          ))}
                        </select>
                        {!showCreateCampaignBulk && (
                          <button
                            type="button"
                            onClick={() => setShowCreateCampaignBulk(true)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            + Create new campaign
                          </button>
                        )}
                        
                        {showCreateCampaignBulk && (
                          <div className="flex gap-2 mt-2">
                            <input
                              type="text"
                              value={newCampaignName}
                              onChange={(e) => setNewCampaignName(e.target.value)}
                              placeholder="Campaign name"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              disabled={creatingCampaign}
                            />
                            <button
                              type="button"
                              onClick={handleCreateCampaign}
                              disabled={creatingCampaign}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                            >
                              {creatingCampaign ? 'Creating...' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowCreateCampaignBulk(false)}
                              className="px-3 py-2 text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* DNC scrub opt-in for eligible tenants now lives in the compliance
                      attestation modal (with pricing), so the paid decision is made in
                      one place right before upload. See the attestation modal below. */}

                  {/* INTEREST CTA — shown to tenants who don't have DNC scrubbing
                      available. One click emails admin@surfox.ai so we can reach
                      out about enabling it. Gated on !loadingCampaigns so it
                      doesn't flash before eligibility resolves. */}
                  {!loadingCampaigns && !dncEligible && (
                    <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <Shield className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-800">
                          Want DNC and litigator scrubbing?
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Automatically exclude numbers on the National DNC or known TCPA litigator lists before importing.
                        </p>
                        {dncInterestSent ? (
                          <p className="text-xs font-medium text-green-700 mt-2 flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Thanks. Our team will reach out about enabling it.
                          </p>
                        ) : (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={handleDncInterest}
                              disabled={dncInterestSending}
                              className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                            >
                              {dncInterestSending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                              {dncInterestSending ? 'Sending...' : "I'm interested"}
                            </button>
                            {dncInterestError && (
                              <p className="text-xs text-red-600 mt-1">{dncInterestError}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CONDITIONAL RENDERING BASED ON STATE */}
                  {selectedFile && showMappingInterface ? (
                    /* MAPPING + PREVIEW — there's no separate "review" step. The
                       sticky modal footer holds the canonical Cancel + Submit. */
                    <div className="space-y-3">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center gap-2 text-sm text-blue-900">
                          <FileText className="w-4 h-4" />
                          <span className="font-medium">{selectedFile.name}</span>
                          <span className="text-blue-700">·</span>
                          <span className="text-blue-700">{parsedRecords.length.toLocaleString()} records</span>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedFile(null);
                            setParsedRecords([]);
                            setFileReady(false);
                            setUploadStatus('');
                            setFieldMapping({});
                            setCsvHeaders([]);
                            setCsvPreviewData([]);
                            setShowMappingInterface(false);
                            sessionStorage.removeItem('addLeadState');
                          }}
                          className="text-xs text-blue-700 hover:text-blue-900 font-medium"
                        >
                          ✕ Remove file
                        </button>
                      </div>

                      <IntelligentFieldMapping
                        csvHeaders={csvHeaders}
                        csvPreviewData={csvPreviewData}
                        mapping={fieldMapping}
                        onMappingChange={setFieldMapping}
                        nameMode={nameMode}
                        onNameModeChange={setNameMode}
                      />
                    </div>
                  ) : (
                    /* UPLOAD STATE - IMPROVED LAYOUT */
                    <>
                      <div className="flex items-center justify-end mb-4">
                        <button
                          onClick={downloadTemplate}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
                        >
                          <Download className="w-4 h-4" />
                          Download Template
                        </button>
                      </div>

                      {/* Drop Zone */}
                      <div
                        className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                          dragActive 
                            ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          accept=".csv"
                          onChange={(e) => handleFileSelect(e.target.files[0])}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        
                        {selectedFile ? (
                          <div className="space-y-3">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                              <FileText className="w-8 h-8 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-lg">{selectedFile.name}</p>
                              <p className="text-sm text-gray-500 mt-1">
                                {(selectedFile.size / 1024).toFixed(1)} KB
                                {parsedRecords.length > 0 && ` • ${parsedRecords.length.toLocaleString()} records`}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFile(null);
                                setParsedRecords([]);
                                setFileReady(false);
                                setUploadStatus('');
                                setFieldMapping({});
                                setCsvHeaders([]);
                                setCsvPreviewData([]);
                                setShowMappingInterface(false);
                                sessionStorage.removeItem('addLeadState');
                              }}
                              className="text-sm text-red-600 hover:text-red-700 font-medium"
                            >
                              ✕ Remove file
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Upload className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Drop your CSV file here</h3>
                            <p className="text-sm text-gray-500 mb-4">or click to browse your computer</p>
                            <div className="inline-flex items-center gap-2 text-xs text-gray-400">
                              <Sparkles className="w-4 h-4" />
                              <span>We'll detect your fields automatically</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Status Messages */}
                      {uploadStatus && (
                        <div className={`p-4 rounded-lg flex items-start gap-3 text-sm ${
                          uploadError 
                            ? 'bg-red-50 border border-red-200 text-red-800' 
                            : 'bg-blue-50 border border-blue-200 text-blue-800'
                        }`}>
                          {uploadError ? (
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Loader2 className="w-5 h-5 flex-shrink-0 mt-0.5 animate-spin" />
                          )}
                          <span>{uploadStatus}</span>
                        </div>
                      )}

                      {/* Guidelines */}
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-blue-600" />
                          How Bulk Import Works
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 font-bold">1.</span>
                            <span>Upload your CSV with any column names</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 font-bold">2.</span>
                            <span>We'll detect Name, Phone, and Email fields automatically</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 font-bold">3.</span>
                            <span>All other columns are preserved as additional lead data</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 font-bold">4.</span>
                            <span>Import processes in background - monitor in "Upload Status" tab</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Upload Status Tab - Keep existing implementation */
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Upload Status</h3>
                      <p className="text-sm text-gray-500">Monitor your bulk upload progress</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={fetchUploadJobs}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Refresh"
                      >
                        <RefreshCw size={16} />
                      </button>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <div className={`w-2 h-2 rounded-full ${pollingActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                        Auto-refresh {pollingActive ? 'on' : 'off'}
                      </div>
                    </div>
                  </div>

                  {jobsError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center text-sm">
                      <AlertCircle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                      <span className="text-red-800">{jobsError}</span>
                    </div>
                  )}

                  {jobsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No uploads yet</p>
                      <p className="text-sm text-gray-500">Start a bulk import to see progress here</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {displayedJobs.map((job) => {
                        const isActive = ['pending', 'processing'].includes(job.status);
                        const isCompleted = job.status === 'completed';
                        const isFailed = job.status === 'failed';
                        
                        const progressPercent = Math.min(job.progress_percentage || 0, 100);
                        const successRate = job.total_records > 0 ? ((job.successful_records || 0) / job.total_records * 100) : 0;
                        
                        return (
                          <div key={job.id} className={`border rounded-lg p-4 transition-all ${
                            isActive ? 'bg-blue-50 border-blue-200' : 
                            isCompleted ? 'bg-white border-gray-200' : 
                            'bg-red-50 border-red-200'
                          }`}>
                            
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`p-1.5 rounded-full ${
                                  isActive ? 'bg-blue-100' :
                                  isCompleted ? 'bg-green-100' : 
                                  'bg-red-100'
                                }`}>
                                  {isActive ? <Activity className="w-4 h-4 text-blue-600" /> :
                                   isCompleted ? <CheckCircle2 className="w-4 h-4 text-green-600" /> :
                                   <AlertCircle className="w-4 h-4 text-red-600" />}
                                </div>
                                
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-gray-900 truncate">{job.file_name}</h4>
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                      isActive ? 'bg-blue-100 text-blue-800' :
                                      isCompleted ? 'bg-green-100 text-green-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {job.status === 'pending' ? 'Queued' :
                                       job.status === 'processing' ? 'Processing' :
                                       job.status === 'completed' ? 'Complete' :
                                       'Failed'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {timeAgo(job.created_at)} • {job.total_records?.toLocaleString() || 0} records
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-1">
                                {job.failed_records > 0 && (
                                  <button
                                    onClick={() => downloadFailedRows(job.id, job.file_name)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Download failed rows"
                                  >
                                    <Download size={14} />
                                  </button>
                                )}
                                {!['pending', 'processing'].includes(job.status) && (
                                  <button
                                    onClick={() => dismissJob(job.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                                    title="Dismiss"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {isActive && (
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>{job.processed_records?.toLocaleString() || 0} / {job.total_records?.toLocaleString() || 0}</span>
                                  <span>{progressPercent.toFixed(0)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${progressPercent}%` }}
                                  />
                                </div>
                                {job.status === 'processing' && job.average_records_per_minute && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    ~{job.average_records_per_minute} records/min • ETA: {formatEstimatedCompletion(job.estimated_completion_at)}
                                  </p>
                                )}
                              </div>
                            )}

                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-4">
                                {job.successful_records > 0 && (
                                  <div className="flex items-center gap-1 text-green-700">
                                    <CheckCircle2 size={14} />
                                    <span className="font-medium">{job.successful_records?.toLocaleString()}</span>
                                    <span className="text-gray-500">success</span>
                                  </div>
                                )}
                                
                                {job.failed_records > 0 && (
                                  <div className="flex items-center gap-1 text-red-700">
                                    <AlertCircle size={14} />
                                    <span className="font-medium">{job.failed_records?.toLocaleString()}</span>
                                    <span className="text-gray-500">failed</span>
                                  </div>
                                )}
                                
                                {job.duplicate_records > 0 && (
                                  <div className="flex items-center gap-1 text-yellow-700">
                                    <Users size={14} />
                                    <span className="font-medium">{job.duplicate_records?.toLocaleString()}</span>
                                    <span className="text-gray-500">already in account</span>
                                  </div>
                                )}
                                
                                {job.opted_out_records > 0 && (
                                  <div className="flex items-center gap-1 text-orange-700">
                                    <AlertCircle size={14} />
                                    <span className="font-medium">{job.opted_out_records?.toLocaleString()}</span>
                                    <span className="text-gray-500">opted out</span>
                                  </div>
                                )}
                              </div>
                              
                              {isCompleted && job.total_records > 0 && (
                                <div className="text-xs text-gray-500">
                                  {successRate.toFixed(1)}% success rate
                                </div>
                              )}
                            </div>

                            {job.scrubbed_count > 0 && job.dnc_download_url && (
                              <div className="mt-3 pt-3 border-t border-amber-200">
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-amber-800 flex items-center gap-1.5">
                                    <Shield size={12} className="text-amber-600" />
                                    DNC/Litigator scrub removed {job.scrubbed_count.toLocaleString()} number{job.scrubbed_count === 1 ? '' : 's'}
                                  </span>
                                  <a
                                    href={job.dnc_download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-amber-700 hover:text-amber-900 font-medium inline-flex items-center gap-1"
                                  >
                                    <Download size={12} />
                                    Download Report
                                  </a>
                                </div>
                              </div>
                            )}

                            {job.line_type_skipped_count > 0 && (
                              <div className="mt-3 pt-3 border-t border-sky-200">
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-sky-800 flex items-center gap-1.5">
                                    <PhoneOff size={12} className="text-sky-600" />
                                    Skipped {job.line_type_skipped_count.toLocaleString()} landline{job.line_type_skipped_count === 1 ? '' : 's'} / unreachable number{job.line_type_skipped_count === 1 ? '' : 's'} (can't receive SMS)
                                  </span>
                                  {job.line_type_download_url && (
                                    <a
                                      href={job.line_type_download_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sky-700 hover:text-sky-900 font-medium inline-flex items-center gap-1"
                                    >
                                      <Download size={12} />
                                      Download Report
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}

                            {job.duplicate_records > 0 && (
                              <div className="mt-3 pt-3 border-t border-indigo-200">
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-indigo-800 flex items-center gap-1.5">
                                    <Users size={12} className="text-indigo-600" />
                                    Skipped {job.duplicate_records.toLocaleString()} lead{job.duplicate_records === 1 ? '' : 's'} already in your account (matched by name + phone)
                                  </span>
                                  {job.duplicate_download_url && (
                                    <a
                                      href={job.duplicate_download_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-indigo-700 hover:text-indigo-900 font-medium inline-flex items-center gap-1"
                                    >
                                      <Download size={12} />
                                      Download Report
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}

                            {isFailed && job.error_message && (
                              <div className="mt-3 pt-3 border-t border-red-200">
                                <p className="text-xs text-red-600 flex items-start gap-1">
                                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                                  <span>{job.error_message}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        );
})}
                    </div>

                    {hasMoreJobs && (
                      <div className="text-center pt-4">
                        <button
                          onClick={() => setShowAllJobs(!showAllJobs)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {showAllJobs ? 'Show Less' : `Show ${visibleJobs.length - displayedJobs.length} More`}
                        </button>
                      </div>
                    )}

                    {hiddenJobIds.size > 0 && (
                      <div className="text-center pt-2">
                        <button
                          onClick={() => setHiddenJobIds(new Set())}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Show {hiddenJobIds.size} dismissed job{hiddenJobIds.size === 1 ? '' : 's'}
                        </button>
                      </div>
                    )}
                  </>
                  )}

                  {jobs.length > 0 && (
                    <div className="grid grid-cols-5 gap-4 pt-4 border-t border-gray-200">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                          {visibleJobs.reduce((sum, job) => sum + (job.total_records || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">Total Records</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-green-600">
                          {visibleJobs.reduce((sum, job) => sum + (job.successful_records || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">Successful</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-red-600">
                          {visibleJobs.reduce((sum, job) => sum + (job.failed_records || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">Failed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-yellow-600">
                          {visibleJobs.reduce((sum, job) => sum + (job.duplicate_records || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">Duplicates</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-orange-600">
                          {visibleJobs.reduce((sum, job) => sum + (job.opted_out_records || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">Opted Out</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {activeTab === 'single' ? (
                <button
                  onClick={handleSingleSubmit}
                  disabled={!form.name || !form.phone || (campaigns.length > 0 && !form.campaign_id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Add Lead
                </button>
              ) : activeTab === 'bulk' ? (
                (() => {
                  // Canonical bulk submit gate: file selected, campaign chosen, phone
                  // mapped, and at least one name field mapped (full_name OR
                  // first_name OR last_name). Customers with only a combined-name
                  // column no longer have to satisfy a required last-name slot.
                  const hasName = !!fieldMapping.full_name || !!fieldMapping.first_name || !!fieldMapping.last_name;
                  const canSubmit = selectedFile && bulkCampaignId && fieldMapping.phone && hasName && !isUploading;
                  return (
                    <button
                      onClick={() => handleBulkSubmit()}
                      disabled={!canSubmit}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Queuing Upload...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Queue {parsedRecords.length.toLocaleString()} Leads
                        </>
                      )}
                    </button>
                  );
                })()
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}